import React, { useEffect, useState } from 'react';
import { BellRing, BellOff, Check, Loader2, TrendingDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { productsAPI, authAPI } from '../services/api';
import { useT } from '../services/i18n';

// « Prévenez-moi » : kind='stock' (back in stock, out-of-stock parts) or kind='price' (price drop, next to the price)
const NotifyMeButton = ({ product, kind = 'stock' }) => {
  const { t, lang } = useT();
  const authed = authAPI.isAuthenticated();
  const isPrice = kind === 'price';
  const k = (key) => t(isPrice ? `notify.price${key[0].toUpperCase()}${key.slice(1)}` : `notify.${key}`);
  const tid = isPrice ? 'price-alert' : 'notify-me';
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | done | already | error
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (!authed || !product?.id) return;
    productsAPI.notifyMeStatus(product.id).then(d => setSubscribed(!!d[kind])).catch(() => {});
  }, [authed, product?.id, kind]);

  const submit = async (e) => {
    e?.preventDefault();
    setState('loading');
    try {
      const d = await productsAPI.notifyMe(product.id, authed ? null : email.trim(), lang, kind);
      setSubscribed(true);
      setState(d.already ? 'already' : 'done');
    } catch {
      setState('error');
    }
  };
  const cancel = async () => {
    setState('loading');
    try { await productsAPI.notifyMeRemove(product.id, kind); setSubscribed(false); setState('idle'); setOpen(false); } catch { setState('error'); }
  };

  const Icon = isPrice ? TrendingDown : BellRing;
  if (subscribed) {
    return (
      <div className={`${isPrice ? 'mt-2' : 'mt-3'} flex flex-wrap items-center gap-2`} data-testid={`${tid}-subscribed`}>
        <span className={`inline-flex items-center gap-1.5 font-medium text-green-700 bg-green-50 border border-green-200 rounded-full ${isPrice ? 'text-xs px-2.5 py-1' : 'text-sm px-3 py-1.5'}`}><Check size={isPrice ? 12 : 14} /> {state === 'done' ? k('done') : state === 'already' ? k('already') : k('subscribed')}</span>
        {authed && <button type="button" onClick={cancel} className="text-xs text-gray-500 hover:text-red-600 underline" data-testid={`${tid}-cancel`}><BellOff size={12} className="inline mr-1" />{t('notify.cancel')}</button>}
        {authed && <Link to="/mon-compte/alertes" className="text-xs text-blue-600 hover:underline" data-testid={`${tid}-manage`}>{t('acc.alerts')}</Link>}
      </div>
    );
  }

  if (!open) {
    return isPrice ? (
      <button type="button" onClick={() => (authed ? submit() : setOpen(true))} disabled={state === 'loading'} data-testid={`${tid}-button`}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1e3a5f] hover:text-orange-600 hover:underline disabled:opacity-60">
        {state === 'loading' ? <Loader2 size={12} className="animate-spin" /> : <TrendingDown size={12} />} {k('button')}
      </button>
    ) : (
      <button type="button" onClick={() => (authed ? submit() : setOpen(true))} disabled={state === 'loading'} data-testid={`${tid}-button`}
        className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#1e3a5f] hover:bg-[#162c48] px-4 py-2 rounded-full transition-colors disabled:opacity-60">
        {state === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} {k('button')}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className={`${isPrice ? 'mt-2' : 'mt-3'} rounded-lg border border-blue-200 bg-blue-50 p-3 max-w-md`} data-testid={`${tid}-form`}>
      <p className="text-sm font-semibold text-[#1e3a5f] flex items-center gap-1.5"><Icon size={14} /> {k('title')}</p>
      <p className="text-xs text-gray-600 mt-1">{k('text')}</p>
      <div className="flex gap-2 mt-2">
        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder={t('notify.emailPh')} data-testid={`${tid}-email`}
          className="flex-1 h-9 text-sm border border-gray-300 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-orange-300" />
        <button type="submit" disabled={state === 'loading'} data-testid={`${tid}-submit`} className="h-9 px-4 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
          {state === 'loading' && <Loader2 size={14} className="animate-spin" />} {t('notify.submit')}
        </button>
      </div>
      {state === 'error' && <p className="text-xs text-red-600 mt-2" data-testid={`${tid}-error`}>{t('notify.error')}</p>}
    </form>
  );
};

export default NotifyMeButton;
